import React, { createContext, useState, ReactNode, useEffect } from 'react';
import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  ListUsersCommand,
  RespondToAuthChallengeCommand
} from '@aws-sdk/client-cognito-identity-provider';
// dummy data for dev
import axios from 'axios';
import { Navigate, useNavigate } from 'react-router-dom';
import { decode, JwtPayload } from 'jsonwebtoken';
import useLocalStorage from '../hooks/localStorage';
import {
  IBilling,
  ICompanyAccount,
  IUserC,
  IUserContext,
  IUserMDB,
  PublicAccountRecoveryResponse,
  UserM
} from '../lib/models/interfaces';
import Cookies from 'js-cookie';
import { JwtToken } from 'aws-sdk/clients/appflow';
import { addRumSessionAttributes } from '../hooks/rum';
import { createLogger } from '../helpers/Logger';
interface Props {
  children?: ReactNode;
}

export const UserContext = createContext({} as IUserContext);

const logger = createLogger({ prefix: '[Auth]' });

const getCognitoLogin = async (email: string, password: string) => {
  const clientCognito = new CognitoIdentityProviderClient({
    region: process.env.REACT_APP_REGION
  });
  const command = new InitiateAuthCommand({
    AuthFlow: 'USER_PASSWORD_AUTH', // You can also use 'USER_PASSWORD_AUTH' if you don't need admin privileges
    ClientId: process.env.REACT_APP_COGNITO_CLIENTID,
    // UserPoolId: process.env.REACT_APP_USERPOOL,
    AuthParameters: {
      USERNAME: email,
      PASSWORD: password
    }
  });
  const response = await clientCognito.send(command);

  return response;
};

const cognitoPasswordChallenge = async (
  email: string,
  password: string,
  Session: string
) => {
  const clientCognito = new CognitoIdentityProviderClient({
    region: process.env.REACT_APP_REGION
  });
  const respondToAuthChallengeCommand = new RespondToAuthChallengeCommand({
    ChallengeName: 'NEW_PASSWORD_REQUIRED',
    ClientId: process.env.REACT_APP_COGNITO_CLIENTID,
    ChallengeResponses: {
      USERNAME: email,
      NEW_PASSWORD: password
    },
    Session: Session
  });

  const challengeResponse = await clientCognito.send(
    respondToAuthChallengeCommand
  );
  return challengeResponse;
};

export const UserContextProvider: React.FC<Props> = (props) => {
  const [user, setUser] = useState<IUserC>({} as IUserC);
  const [token, setToken] = useState<string>('');
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isDeveloper, setIsDeveloper] = useState<boolean>(false);
  const [storedToken, setStoredToken] = useLocalStorage<string>('token', '');
  const [storedAccessToken, setStoredAccessToken] = useLocalStorage<string>(
    'accessToken',
    ''
  );
  const [userM, setUserM] = useState<IUserMDB>({} as IUserMDB);
  // const [storedRefreshToken, setStoredRefreshToken] = useLocalStorage<string>(
  //   'refreshToken',
  //   ''
  // );
  const [storedUser, setStoredUser] = useLocalStorage<string>('user', '');
  const [cognitoChallenge, setCognitoChallenge] = useState<boolean>(false);
  const [challengeSession, setChallengeSession] = useState<string>('');
  const [passwordUserError, setPasswordUserError] = useState<boolean>(false);
  const [expiryTime, setExpiryTime] = useState<number>(0);
  const [users, setUsers] = useState<IUserMDB[]>([]);
  const [latestTNCAcknowledged, setLatestTNCAcknowledged] =
    useState<boolean>(false);
  const [userChecksComplete, setUserChecksComplete] = useState<boolean>(false);
  const [showNoticeModal, setShowNoticeModal] = useState<boolean>(false);
  const [companyAccount, setCompanyAccount] = useState<ICompanyAccount | null>(
    null
  );
  const [subscription, setSubscription] = useState<IBilling | null>(null);
  const [authHydrated, setAuthHydrated] = useState<boolean>(false);
  const [countryAccepted, setCountryAccepted] = useState<boolean>(false);
  const [privacyAccepted, setPrivacyAccepted] = useState<boolean>(false);
  const [authHydratedError, setAuthHydratedError] = useState<boolean>(false);

  useEffect(() => {
    const now = Math.floor(Date.now() / 1000);
    const diff =
      (expiryTime - now - Math.floor(Math.random() * (15 - 1 + 1)) + 1) * 1000;
    const timer = setTimeout(() => {
      const refreshToken = Cookies.get('refreshToken');

      if (refreshToken) {
        getNewTokenFromRefresh(refreshToken);
      } else {
        logger.info('no refresh token');
      }
    }, diff);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expiryTime]);

  const getNewTokenFromRefresh = async (refreshToken: string) => {
    logger.info('running refresh token');
    try {
      const command = new InitiateAuthCommand({
        AuthFlow: 'REFRESH_TOKEN_AUTH', // Specify the auth flow as REFRESH_TOKEN_AUTH
        ClientId: process.env.REACT_APP_COGNITO_CLIENTID,
        AuthParameters: {
          REFRESH_TOKEN: refreshToken // Pass the refresh token
        }
      });
      const clientCognito = new CognitoIdentityProviderClient({
        region: process.env.REACT_APP_REGION
      });
      const refreshResponse = await clientCognito.send(command);
      if (refreshResponse.AuthenticationResult?.IdToken) {
        setToken(refreshResponse.AuthenticationResult?.IdToken);
        setStoredToken(refreshResponse.AuthenticationResult?.IdToken);
        setUserFromToken(refreshResponse.AuthenticationResult?.IdToken);
        setStoredAccessToken(
          refreshResponse.AuthenticationResult?.AccessToken!
        );
        Cookies.set('token', refreshResponse.AuthenticationResult?.IdToken);
      }
    } catch (e) {
      const error = e as Error;
      logger.error(error.message);
      Cookies.remove('token');
      if (error.message === 'Refresh Token has expired') {
        Cookies.remove('refreshToken');
        logout();
      }
      // setStoredRefreshToken('');
      setToken('');
      setStoredAccessToken('');
    }
  };

  const getUsers = async () => {
    try {
      const results = await axios.post(
        `${process.env.REACT_APP_KORU_API}/user?type=getAll`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setUsers(results.data.list);
    } catch (e) {
      const error = e as Error;
      throw Error(error.message);
    }
  };
  const isExpired = (token: JwtToken): boolean => {
    if (!token) return true;

    const payload = decode(token) as JwtPayload | null;
    if (!payload?.exp) return true;

    const now = Math.floor(Date.now() / 1000);
    return now >= payload.exp;
  };
  const isLatestAcknowledged = (user: UserM) => {
    if (user?.tncAcknowledge?.length === 0) return false;
    const latest = user?.tncAcknowledge?.pop();
    logger.info('Lastest ==============', latest);
    if (latest?.version === process.env.REACT_APP_TNC_VERSION) {
      logger.info('returning true');
      return true;
    }
    return false;
  };
  const getAndSetUserM = async () => {
    if (!token) return;
    try {
      const result = await axios.post(
        `${process.env.REACT_APP_KORU_API}/user?type=getOneUser`,
        {},
        auth
      );
      if (result.status === 200) {
        logger.info('user info', result.data);
        Cookies.set('userM', JSON.stringify(result.data), { expires: 7 });
        setUserM(result.data);
        const latest = isLatestAcknowledged(result.data);
        if (latest) {
          setLatestTNCAcknowledged(true);
        } else {
          setLatestTNCAcknowledged(false);
        }
      }
      setUserChecksComplete(true);
    } catch (e) {
      logger.error('error from get user', e);
    }
  };
  useEffect(() => {
    getAndSetUserM();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  // const getAccountInfo = async() => {
  //   try{
  //     const result = await axios.post(
  //      `${process.env.REACT_APP_KORU_API}/account?type=getAccountDetails`,
  //       {customerId: },
  //       auth
  //     );
  //   }
  // }
  // const getAndTestUserM = async () => {
  //   const userM = Cookies.get('userM');
  //   logger.info('userm in get and test', userM);
  //   logger.info('userM length', userM?.trim().length);
  //   const validUserM = typeof userM === 'string' && userM.trim().length > 0;
  //   logger.info('validUserM', validUserM);
  //   if (!validUserM) {
  //     logger.info('undefined userM');
  //     await getAndSetUserM();
  //   } else {
  //     logger.info('Using this user M', userM);
  //     const parsedUser = JSON.parse(userM);
  //     const latest = isLatestAcknowledged(parsedUser);
  //     logger.info('in get and test userM latst value', latest);
  //     if (latest) {
  //       setLatestTNCAcknowledged(true);
  //     } else {
  //       setLatestTNCAcknowledged(false);
  //     }
  //     setUserChecksComplete(true);
  //   }
  // };
  useEffect(() => {
    const initialCheck = async () => {
      const currentToken = Cookies.get('token');
      const refreshToken = Cookies.get('refreshToken');
      const cookieUserM = Cookies.get('userM');
      logger.info('calling isExpired (combined effect)');

      if (currentToken && !isExpired(currentToken as unknown as JwtToken)) {
        setToken(currentToken);
        setUserFromToken(currentToken);
      } else if (refreshToken) {
        // If we have a token, check if it's within a grace period and try refresh
        if (currentToken) {
          const decodedToken = decode(currentToken) as unknown as JwtPayload;
          logger.info('decoded token', decodedToken);
          if (decodedToken.exp! > Math.floor(Date.now() / 1000) + 60) {
            await getNewTokenFromRefresh(refreshToken);
            return;
          }
        }
        await getNewTokenFromRefresh(refreshToken);
      }
    };
    initialCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAccountInfo = async () => {
    try {
      const result = await axios.post(
        `${process.env.REACT_APP_KORU_API}/account?type=getAccountDetails`,
        {},
        auth
      );

      const data = result.data.result[0] as unknown as ICompanyAccount;
      logger.info('account from get', data);
      // this needs to confirm a valid account is setup.
      setCompanyAccount(data);
      setSubscription({
        status: data?.subscriptions.at(-1)?.status!,
        price: data?.PriceInfo
      });
    } catch (e) {
      logger.error('error in get account info', e);
    }
  };
  const hydrateAuth = async () => {
    if (!token) return;
    try {
      logger.info('Hydrate Auth Running');
      // Run these in parallel; none depends on the others
      const results = await Promise.allSettled([
        getUsers(),
        // getAndTestUserM(),
        getAndSetUserM(),

        getAccountInfo()
      ]);
      // Optional: error any failures for debugging without blocking hydration
      results.forEach((r, i) => {
        if (r.status === 'rejected') {
          logger.warn('hydrateAuth task failed', i, r.reason);
          throw Error('Failed');
        }
      });
      addRumSessionAttributes({ koruUserSub: user.sub });
      setAuthHydrated(true);
    } catch (e) {
      // This catch is mostly defensive; Promise.allSettled won't throw
      logger.error('error in hydrate', e);
      setAuthHydrated(true);
      setAuthHydratedError(true);
    }
  };
  useEffect(() => {
    logger.info('calling is xpired 247');
    if (token && !isExpired(token)) {
      hydrateAuth();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const setUserFromToken = (token: string) => {
    const user = decode(token) as unknown as JwtPayload;
    const userObject: IUserC = {
      username: user?.name,
      name: user?.name,
      customerId: user['custom:customerId'],
      isAdmin: user['custom:isAdmin'] === 'true' ? true : false,
      email: user?.email,
      groups: user[`cognito:groups`] ? user[`cognito:groups`] : [],
      sub: user.sub!,
      reseller:
        user[`custom:reseller`] && user[`custom:reseller`] === 'true'
          ? true
          : false,
      freeTrialKey: user['custom:freeTrialKey'] ?? null
    };
    // this sets the expiry time from the token.
    setExpiryTime(user.exp!);
    setIsAdmin(userObject.isAdmin);
    setUser(userObject);
    if (userObject.groups.includes('developer')) {
      setIsDeveloper(true);
    }
    Cookies.set('user', JSON.stringify(userObject));
  };

  useEffect(() => {
    const localToken = Cookies.get('token');
    if (localToken) {
      setUserFromToken(localToken);
    }
  }, []);

  const getSignIn = async (email: string, password: string, save: boolean) => {
    try {
      if (challengeSession) {
        const challengeResult = await cognitoPasswordChallenge(
          email,
          password,
          challengeSession
        );
        if (challengeResult?.AuthenticationResult) {
          setToken(challengeResult.AuthenticationResult?.IdToken!);
          Cookies.set('token', challengeResult.AuthenticationResult?.IdToken!);
          setStoredToken(challengeResult.AuthenticationResult?.IdToken!);
          // setStoredRefreshToken(
          //   challengeResult.AuthenticationResult?.RefreshToken!
          // );
          setUserFromToken(challengeResult.AuthenticationResult?.IdToken!);
          setStoredAccessToken(
            challengeResult.AuthenticationResult?.AccessToken!
          );
          Cookies.set(
            'refreshToken',
            challengeResult.AuthenticationResult?.RefreshToken!
          );
          Cookies.set('token', challengeResult.AuthenticationResult?.IdToken!);
        }
      }
      try {
        const cognitoT = await getCognitoLogin(email, password);
        if (cognitoT.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
          setCognitoChallenge(true);
          setChallengeSession(cognitoT?.Session!);
        } else {
          // setCognitoToken(cognitoT.AuthenticationResult?.IdToken!);
          setToken(cognitoT.AuthenticationResult?.IdToken!);
          setUserFromToken(cognitoT.AuthenticationResult?.IdToken!);
          setStoredToken(cognitoT.AuthenticationResult?.IdToken!);
          // setStoredRefreshToken(cognitoT.AuthenticationResult?.RefreshToken!);
          setStoredAccessToken(cognitoT.AuthenticationResult?.AccessToken!);
          Cookies.set(
            'refreshToken',
            cognitoT.AuthenticationResult?.RefreshToken!
          );
          Cookies.set('token', cognitoT.AuthenticationResult?.IdToken!);
          logger.info('calling is xpired 349');
          logger.info(
            'Token expiration',
            isExpired(cognitoT.AuthenticationResult?.IdToken!)
          );
          // window.location.replace(
          //   `${process.env.REACT_APP_BASE_URL}/app/landing`
          // );
        }
      } catch (e) {
        const error = e as Error;
        if (error.message === 'Incorrect username or password.') {
          setPasswordUserError(true);
        }
        if (error.message === 'User is disabled.') {
          setPasswordUserError(true);
        }
      }
    } catch (e) {
      logger.error('signin error', e);
    }
  };

  const updateTNC = async () => {
    const result = await axios.post(
      `${process.env.REACT_APP_KORU_API}/user?type=updateTNC`,
      { acknowledge: { version: '1.0', date: Date.now() } },
      auth
    );
    Cookies.set('userM', JSON.stringify(result.data), { expires: 7 });
    return result;
  };

  const logout = () => {
    setToken('');
    setUser({} as IUserC);
    setStoredToken('');
    setStoredUser('');
    setIsDeveloper(false);
    setIsAdmin(false);
    setStoredAccessToken('');
    Cookies.remove('token');
    Cookies.remove('refreshToken');
    Cookies.remove('user');
    Cookies.remove('userM');
    const logoutUrl = window.location.replace(
      `${process.env.REACT_APP_BASE_URL}/login`
    );
  };

  const updateToken = async () => {
    const refreshToken = Cookies.get('refreshToken');
    logger.info('in update token, ', refreshToken);
    if (!refreshToken) {
      logout();
    } else {
      logger.info('getting new token');
      setToken('');
      Cookies.remove('token');
      Cookies.remove('user');
      Cookies.remove('userM');
      await getNewTokenFromRefresh(refreshToken);
    }
  };

  const getCognitoLostPassword = async (
    email: string
  ): Promise<PublicAccountRecoveryResponse> => {
    try {
      const response = await axios.post<PublicAccountRecoveryResponse>(
        `${process.env.REACT_APP_KORU_API}/account/recover`,
        { email }
      );
      logger.info('account recovery response', response.data);
      return response.data;
    } catch (e) {
      const error = e as Error;
      logger.error('error', error);
      throw e;
    }
  };

  const auth = {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };

  const cognitoAuth = {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };

  const value = {
    user,
    userM,
    getAndSetUserM,
    token,
    setToken,
    getSignIn,
    logout,
    auth,
    // createAccount,
    isAdmin,
    isDeveloper,
    cognitoChallenge,
    passwordUserError,
    isExpired,
    users,
    getUsers,
    setUsers,
    updateTNC,
    latestTNCAcknowledged,
    setLatestTNCAcknowledged,
    userChecksComplete,
    showNoticeModal,
    setShowNoticeModal,
    updateToken,
    companyAccount,
    setCompanyAccount,
    subscription,
    setSubscription,
    authHydrated,
    getCognitoLostPassword,
    rehydrateAuth: hydrateAuth,
    countryAccepted,
    setCountryAccepted,
    privacyAccepted,
    setPrivacyAccepted,
    authHydratedError
  };

  return (
    <UserContext.Provider value={value}>{props.children}</UserContext.Provider>
  );
};
